import {
  AUTO_MATCH_COUNTDOWN_MS,
  AUTO_MATCH_PLAYERS,
  CHECKPOINT_INTERVAL_MS,
  HEALTH_PICKUP_RESTORE_RATIO,
  HOMING_PICKUP_AMOUNT,
  HOMING_TORPEDO_COOLDOWN_MS,
  HOMING_TORPEDO_DAMAGE,
  HOMING_TORPEDO_LIFESPAN_MS,
  HOMING_TORPEDO_SPEED,
  HOMING_TORPEDO_TURN_SPEED,
  MATCH_DURATION_MS,
  MAX_PLAYERS,
  MINE_COOLDOWN_MS,
  MINE_DAMAGE,
  MINE_LIFESPAN_MS,
  MINE_PICKUP_AMOUNT,
  MINE_RADIUS,
  MIN_PLAYERS_TO_START,
  PICKUP_LIFESPAN_MS,
  PICKUP_RADIUS,
  PLAYER_ACCELERATION,
  PLAYER_FRICTION,
  PLAYER_INITIAL_HOMING,
  PLAYER_INITIAL_MINES,
  PLAYER_MAX_HEALTH,
  PLAYER_MAX_SPEED,
  PLAYER_RADIUS,
  PLAYER_TURN_SPEED,
  PLAYER_VERTICAL_FRICTION,
  PLAYER_VERTICAL_SPEED,
  PROJECTILE_RADIUS,
  RECONNECT_GRACE_MS,
  REGULAR_TORPEDO_COOLDOWN_MS,
  REGULAR_TORPEDO_DAMAGE,
  REGULAR_TORPEDO_LIFESPAN_MS,
  REGULAR_TORPEDO_SPEED,
  RESPAWN_MS,
  SCORE_LIMIT,
  SEABED_Y,
  SIMULATION_STEP_MS,
  SPAWN_PROTECTION_MS,
  SURFACE_Y,
  WORLD_BOUNDS_XZ
} from "./constants";
import type {
  ChatMessage,
  GameEvent,
  GameMode,
  GameSnapshot,
  MineState,
  MatchResult,
  PickupKind,
  PickupState,
  PlayerInput,
  PlayerLoadout,
  PlayerState,
  ProjectileState,
  RoomConfig,
  RoomPublicState,
  RoomKind,
  RoomState,
  ServerPlayerState,
  TeamId,
  Vector3State
} from "./types";

type SpawnPoint = Vector3State & { rotation: number };

const SPAWNS: SpawnPoint[] = [
  { x: -24, y: -18, z: 0, rotation: Math.PI },
  { x: 24, y: -18, z: 0, rotation: 0 },
  { x: 0, y: -24, z: -24, rotation: Math.PI / 2 },
  { x: 0, y: -24, z: 24, rotation: -Math.PI / 2 }
];

const ZERO_INPUT: PlayerInput = {
  seq: 0,
  clientTime: 0,
  thrust: 0,
  turn: 0,
  vertical: 0,
  fire: false,
  homing: false,
  mine: false
};

const DEFAULT_LOADOUT: PlayerLoadout = { hull: "assault", skin: "classic" };

export function defaultRoomConfig(options: Partial<RoomConfig> = {}): RoomConfig {
  const mode = normalizeGameMode(options.mode);
  const maxPlayers = mode === "team_deathmatch" ? Math.max(2, normalizeMaxPlayers(options.maxPlayers ?? MAX_PLAYERS)) : normalizeMaxPlayers(options.maxPlayers ?? MAX_PLAYERS);
  return {
    mode,
    maxPlayers,
    scoreLimit: normalizePositiveInteger(options.scoreLimit, SCORE_LIMIT, 1, 50),
    durationMs: normalizePositiveInteger(options.durationMs, MATCH_DURATION_MS, 60_000, 20 * 60 * 1000),
    mapSeed: sanitizeMapSeed(options.mapSeed ?? "classic-trench"),
    friendlyFire: Boolean(options.friendlyFire)
  };
}

export function createRoomState(roomId: string, now: number, options: { roomKind?: RoomKind; maxPlayers?: number; config?: Partial<RoomConfig> } = {}): RoomState {
  const roomKind = options.roomKind ?? "private";
  const config = defaultRoomConfig({ ...options.config, maxPlayers: roomKind === "auto" ? AUTO_MATCH_PLAYERS : options.config?.maxPlayers ?? options.maxPlayers });
  return {
    roomId,
    roomKind,
    config,
    phase: "lobby",
    tick: 0,
    createdAt: now,
    autoStartAt: null,
    startedAt: null,
    endsAt: null,
    finishedAt: null,
    winnerId: null,
    winningTeamId: null,
    rematchRequestedBy: [],
    chat: [],
    maxPlayers: roomKind === "auto" ? AUTO_MATCH_PLAYERS : config.maxPlayers,
    players: [],
    projectiles: [],
    mines: [],
    pickups: [],
    inputs: {},
    lastCheckpointAt: now,
    disconnectGraceUntilByPlayer: {}
  };
}

export function sanitizeRoomId(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 16);
}

export function sanitizePlayerName(value: string): string {
  const clean = value.trim().replace(/\s+/g, " ").slice(0, 18);
  return clean || "Captain";
}

export function addOrReconnectPlayer(
  room: RoomState,
  params: { id: string; token: string; name: string; now: number; profileId?: string | null }
): ServerPlayerState {
  ensureRoomCollections(room);
  const name = sanitizePlayerName(params.name);
  const byToken = room.players.find((player) => player.token === params.token);
  if (byToken) {
    byToken.name = name;
    byToken.profileId = params.profileId ?? byToken.profileId ?? null;
    byToken.connected = true;
    clearReconnectGrace(room, byToken.id);
    return byToken;
  }

  if (room.phase !== "lobby") {
    throw new Error("Match already started. Reconnect with your original room token.");
  }

  if (room.players.length >= roomMaxPlayers(room)) {
    throw new Error("Room is full.");
  }

  const slot = nextOpenSlot(room);
  const player = createServerPlayer(params.id, params.token, name, slot, params.now, params.profileId ?? null, room.config.mode);
  room.players.push(player);
  room.inputs[player.id] = { ...ZERO_INPUT };
  assignTeams(room);
  return player;
}

export function addReservedPlayer(
  room: RoomState,
  params: { id: string; token: string; name: string; now: number; profileId?: string | null }
): ServerPlayerState {
  ensureRoomCollections(room);
  const existing = room.players.find((player) => player.token === params.token || player.id === params.id);
  if (existing) return existing;
  if (room.phase !== "lobby") {
    throw new Error("Match already started.");
  }
  if (room.players.length >= roomMaxPlayers(room)) {
    throw new Error("Room is full.");
  }

  const player = createServerPlayer(params.id, params.token, sanitizePlayerName(params.name), nextOpenSlot(room), params.now, params.profileId ?? null, room.config.mode);
  player.connected = false;
  player.ready = false;
  room.players.push(player);
  room.inputs[player.id] = { ...ZERO_INPUT };
  assignTeams(room);
  return player;
}

export function setPlayerConnected(room: RoomState, playerId: string, connected: boolean, now: number): GameEvent | null {
  const player = room.players.find((candidate) => candidate.id === playerId);
  if (!player) return null;
  if (!connected && !player.connected) return null;
  player.connected = connected;
  if (!connected) {
    player.ready = false;
    room.inputs[player.id] = { ...ZERO_INPUT };
    room.disconnectGraceUntilByPlayer ??= {};
    room.disconnectGraceUntilByPlayer[player.id] = now + RECONNECT_GRACE_MS;
  } else {
    clearReconnectGrace(room, player.id);
  }
  return makeEvent(connected ? "player_joined" : "player_left", now, connected ? `${player.name} connected` : `${player.name} disconnected`, {
    playerId
  });
}

export function setPlayerReady(room: RoomState, playerId: string, ready: boolean, now: number): GameEvent | null {
  if (room.phase !== "lobby") return null;
  if (roomKind(room) === "auto") return null;
  const player = room.players.find((candidate) => candidate.id === playerId);
  if (!player) return null;
  player.ready = ready;
  return makeEvent("player_ready", now, `${player.name} is ${ready ? "ready" : "not ready"}`, {
    playerId,
    value: ready ? 1 : 0
  });
}

export function syncAutoStartCountdown(room: RoomState, now: number): boolean {
  if (room.phase !== "lobby" || roomKind(room) !== "auto") {
    if (room.autoStartAt === null) return false;
    room.autoStartAt = null;
    return true;
  }

  const connectedCount = room.players.filter((player) => player.connected).length;
  if (connectedCount < roomMaxPlayers(room)) {
    if (room.autoStartAt === null) return false;
    room.autoStartAt = null;
    return true;
  }

  if (room.autoStartAt !== null) return false;
  room.autoStartAt = now + AUTO_MATCH_COUNTDOWN_MS;
  return true;
}

export function tryStartMatch(room: RoomState, now: number): GameEvent | null {
  ensureRoomCollections(room);
  if (room.phase !== "lobby") return null;
  const connected = room.players.filter((player) => player.connected);
  if (roomKind(room) === "auto") {
    if (connected.length < roomMaxPlayers(room)) return null;
    if (room.autoStartAt === null || now < room.autoStartAt) return null;
  } else {
    if (connected.length < MIN_PLAYERS_TO_START) return null;
    if (!connected.every((player) => player.ready)) return null;
  }

  room.phase = "playing";
  room.autoStartAt = null;
  room.startedAt = now;
  room.endsAt = now + room.config.durationMs;
  room.finishedAt = null;
  room.winnerId = null;
  room.winningTeamId = null;
  room.tick = 0;
  room.projectiles = [];
  room.mines = [];
  room.pickups = [];

  for (const player of room.players) {
    resetPlayerForSpawn(player, now, false);
    player.score = 0;
    player.ready = false;
  }
  room.rematchRequestedBy = [];

  return makeEvent("match_started", now, "Match started");
}

export function applyInput(room: RoomState, playerId: string, input: PlayerInput): void {
  const player = room.players.find((candidate) => candidate.id === playerId);
  if (!player || !player.connected || room.phase !== "playing" || input.seq <= player.lastInputSeq) return;

  const cleanInput: PlayerInput = {
    seq: Math.max(0, Math.floor(input.seq)),
    clientTime: Number.isFinite(input.clientTime) ? input.clientTime : 0,
    thrust: clamp(input.thrust, -1, 1),
    turn: clamp(input.turn, -1, 1),
    vertical: clamp(input.vertical, -1, 1),
    fire: Boolean(input.fire),
    homing: Boolean(input.homing),
    mine: Boolean(input.mine)
  };

  player.lastInputSeq = cleanInput.seq;
  room.inputs[playerId] = cleanInput;
}

export function stepRoom(room: RoomState, now: number): GameEvent[] {
  const events: GameEvent[] = [];
  ensureRoomCollections(room);
  if (room.phase !== "playing") return events;

  room.tick += 1;

  for (const player of room.players) {
    if (!player.connected) continue;
    if (!player.alive && player.respawnAt !== null && now >= player.respawnAt) {
      resetPlayerForSpawn(player, now, true);
      events.push(makeEvent("player_respawned", now, `${player.name} resurfaced`, { playerId: player.id }));
    }
  }

  for (const player of room.players) {
    updatePlayer(room, player, now, events);
  }

  updateProjectiles(room, now, events);
  updateMines(room, now, events);
  updatePickups(room, now, events);

  if (room.endsAt !== null && now >= room.endsAt) {
    finishMatch(room, now, events);
  }

  return events;
}

export function shouldCheckpoint(room: RoomState, now: number): boolean {
  return now - room.lastCheckpointAt >= CHECKPOINT_INTERVAL_MS;
}

export function toSnapshot(room: RoomState, events: GameEvent[], now: number, options: { spectatorCount?: number } = {}): GameSnapshot {
  return {
    tick: room.tick,
    serverTime: now,
    roomId: room.roomId,
    roomKind: roomKind(room),
    matchState: room.phase,
    players: toPublicPlayers(room),
    projectiles: room.projectiles.map((projectile) => ({ ...projectile, position: { ...projectile.position }, velocity: { ...projectile.velocity } })),
    mines: room.mines.map((mine) => ({ ...mine, position: { ...mine.position } })),
    pickups: (room.pickups ?? []).map((pickup) => ({ ...pickup, position: { ...pickup.position } })),
    events,
    autoStartAt: room.autoStartAt ?? null,
    endsAt: room.endsAt,
    lastInputSeqByPlayer: Object.fromEntries(room.players.map((player) => [player.id, player.lastInputSeq])),
    matchResult: room.phase === "finished" && room.finishedAt ? toMatchResult(room, room.finishedAt) : null,
    scoreLimit: room.config.scoreLimit,
    config: room.config,
    maxPlayers: roomMaxPlayers(room),
    spectatorCount: options.spectatorCount ?? 0,
    hostPlayerId: hostPlayerId(room),
    rematchRequestedBy: [...(room.rematchRequestedBy ?? [])]
  };
}

export function toPublicRoom(room: RoomState, options: { spectatorCount?: number } = {}): RoomPublicState {
  return {
    roomId: room.roomId,
    roomKind: roomKind(room),
    config: room.config,
    phase: room.phase,
    players: toPublicPlayers(room),
    createdAt: room.createdAt,
    autoStartAt: room.autoStartAt ?? null,
    startedAt: room.startedAt,
    endsAt: room.endsAt,
    rematchRequestedBy: [...(room.rematchRequestedBy ?? [])],
    chat: [...(room.chat ?? [])],
    scoreLimit: room.config.scoreLimit,
    maxPlayers: roomMaxPlayers(room),
    spectatorCount: options.spectatorCount ?? 0,
    hostPlayerId: hostPlayerId(room)
  };
}

function toPublicPlayers(room: RoomState): PlayerState[] {
  return room.players.map((player) => ({
    ...toPublicPlayer(player),
    reconnectGraceUntil: room.disconnectGraceUntilByPlayer?.[player.id] ?? null
  }));
}

export function toPublicPlayer(player: ServerPlayerState): PlayerState {
  return {
    id: player.id,
    name: player.name,
    profileId: player.profileId ?? null,
    slot: player.slot,
    teamId: player.teamId ?? null,
    loadout: player.loadout ?? DEFAULT_LOADOUT,
    position: { ...player.position },
    rotation: player.rotation,
    velocity: { ...player.velocity },
    health: player.health,
    ammo: { ...player.ammo },
    score: player.score,
    alive: player.alive,
    ready: player.ready,
    connected: player.connected,
    invulnerableUntil: player.invulnerableUntil,
    respawnAt: player.respawnAt
  };
}

function clearReconnectGrace(room: RoomState, playerId: string): void {
  if (!room.disconnectGraceUntilByPlayer) return;
  delete room.disconnectGraceUntilByPlayer[playerId];
}

function hostPlayerId(room: RoomState): string | null {
  return [...room.players].sort((a, b) => a.slot - b.slot)[0]?.id ?? null;
}

export function setPlayerLoadout(room: RoomState, playerId: string, loadout: PlayerLoadout): GameEvent | null {
  const player = room.players.find((candidate) => candidate.id === playerId);
  if (!player || room.phase !== "lobby") return null;
  player.loadout = sanitizeLoadout(loadout);
  return makeEvent("player_ready", Date.now(), `${player.name} updated loadout`, { playerId });
}

export function addChatMessage(room: RoomState, playerId: string, message: string, now: number): ChatMessage | null {
  const player = room.players.find((candidate) => candidate.id === playerId);
  const clean = sanitizeChatMessage(message);
  if (!player || !clean) return null;
  ensureRoomCollections(room);
  const chat = {
    id: `${now}-chat-${player.id}-${Math.random().toString(36).slice(2, 7)}`,
    playerId: player.id,
    playerName: player.name,
    message: clean,
    at: now
  };
  room.chat.push(chat);
  room.chat = room.chat.slice(-30);
  return chat;
}

export function requestRematch(room: RoomState, playerId: string): boolean {
  if (room.phase !== "finished" || !room.players.some((player) => player.id === playerId)) return false;
  room.rematchRequestedBy ??= [];
  if (!room.rematchRequestedBy.includes(playerId)) room.rematchRequestedBy.push(playerId);
  const connectedPlayers = room.players.filter((player) => player.connected);
  return connectedPlayers.length >= MIN_PLAYERS_TO_START && connectedPlayers.every((player) => room.rematchRequestedBy.includes(player.id));
}

export function resetForRematch(room: RoomState, now: number): GameEvent {
  ensureRoomCollections(room);
  room.phase = "lobby";
  room.startedAt = null;
  room.endsAt = null;
  room.finishedAt = null;
  room.winnerId = null;
  room.winningTeamId = null;
  room.projectiles = [];
  room.mines = [];
  room.pickups = [];
  room.rematchRequestedBy = [];
  for (const player of room.players) {
    room.inputs[player.id] = { ...ZERO_INPUT };
    resetPlayerForSpawn(player, now, false);
    player.score = 0;
    player.ready = room.roomKind === "auto";
  }
  return makeEvent("match_started", now, "Rematch lobby ready");
}

export function toMatchResult(room: RoomState, now: number): MatchResult {
  const standings = [...room.players]
    .map((player) => ({ playerId: player.id, name: player.name, teamId: player.teamId ?? null, score: player.score, connected: player.connected }))
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
  const mvp = standings[0] ?? null;
  return {
    matchId: `${room.roomId}-${room.startedAt ?? room.createdAt}`,
    roomId: room.roomId,
    roomKind: roomKind(room),
    config: room.config,
    startedAt: room.startedAt,
    finishedAt: room.finishedAt ?? now,
    winnerId: room.winnerId,
    winningTeamId: room.winningTeamId ?? null,
    mvpPlayerId: mvp?.playerId ?? null,
    standings
  };
}

function createServerPlayer(id: string, token: string, name: string, slot: number, now: number, profileId: string | null, mode: GameMode): ServerPlayerState {
  const spawn = spawnForSlot(slot);
  const player: ServerPlayerState = {
    id,
    token,
    name,
    profileId,
    slot,
    teamId: teamForSlot(slot, mode),
    loadout: DEFAULT_LOADOUT,
    position: positionFromSpawn(spawn),
    rotation: spawn.rotation,
    velocity: { x: 0, y: 0, z: 0 },
    health: PLAYER_MAX_HEALTH,
    ammo: { homing: PLAYER_INITIAL_HOMING, mines: PLAYER_INITIAL_MINES },
    score: 0,
    alive: true,
    ready: false,
    connected: true,
    invulnerableUntil: now + SPAWN_PROTECTION_MS,
    respawnAt: null,
    lastInputSeq: 0,
    lastFireAt: 0,
    lastHomingAt: 0,
    lastMineAt: 0
  };
  return player;
}

function updatePlayer(room: RoomState, player: ServerPlayerState, now: number, events: GameEvent[]): void {
  if (!player.alive) {
    consumeOneShotWeaponInput(room, player.id);
    return;
  }
  const input = room.inputs[player.id] ?? ZERO_INPUT;
  const dt = SIMULATION_STEP_MS / 1000;

  player.rotation = normalizeAngle(player.rotation + input.turn * PLAYER_TURN_SPEED * dt);
  const forward = forwardFromRotation(player.rotation);
  player.velocity.x += forward.x * input.thrust * PLAYER_ACCELERATION * dt;
  player.velocity.z += forward.z * input.thrust * PLAYER_ACCELERATION * dt;
  player.velocity.y += input.vertical * PLAYER_VERTICAL_SPEED * dt;

  const planarSpeed = Math.hypot(player.velocity.x, player.velocity.z);
  if (planarSpeed > PLAYER_MAX_SPEED) {
    const scale = PLAYER_MAX_SPEED / planarSpeed;
    player.velocity.x *= scale;
    player.velocity.z *= scale;
  }

  player.position.x = clamp(player.position.x + player.velocity.x * dt, -WORLD_BOUNDS_XZ, WORLD_BOUNDS_XZ);
  player.position.y = clamp(player.position.y + player.velocity.y * dt, SEABED_Y + PLAYER_RADIUS, SURFACE_Y);
  player.position.z = clamp(player.position.z + player.velocity.z * dt, -WORLD_BOUNDS_XZ, WORLD_BOUNDS_XZ);

  player.velocity.x = applyFriction(player.velocity.x, PLAYER_FRICTION, dt);
  player.velocity.z = applyFriction(player.velocity.z, PLAYER_FRICTION, dt);
  player.velocity.y = applyFriction(player.velocity.y, PLAYER_VERTICAL_FRICTION, dt);

  if (input.fire && now - player.lastFireAt >= REGULAR_TORPEDO_COOLDOWN_MS) {
    player.lastFireAt = now;
    room.projectiles.push(createProjectile(room, player, "regular", now));
    events.push(makeEvent("torpedo_fired", now, `${player.name} fired a torpedo`, { playerId: player.id, position: { ...player.position }, source: "regular" }));
  }

  if (input.homing && player.ammo.homing > 0 && now - player.lastHomingAt >= HOMING_TORPEDO_COOLDOWN_MS) {
    player.lastHomingAt = now;
    player.ammo.homing -= 1;
    room.projectiles.push(createProjectile(room, player, "homing", now));
    events.push(makeEvent("torpedo_fired", now, `${player.name} fired a homing torpedo`, { playerId: player.id, position: { ...player.position }, source: "homing" }));
  }

  if (input.mine && player.ammo.mines > 0 && now - player.lastMineAt >= MINE_COOLDOWN_MS) {
    player.lastMineAt = now;
    player.ammo.mines -= 1;
    room.mines.push({
      id: `${room.tick}-mine-${player.id}-${player.lastMineAt}`,
      ownerId: player.id,
      position: { ...player.position },
      radius: MINE_RADIUS,
      damage: MINE_DAMAGE,
      expiresAt: now + MINE_LIFESPAN_MS
    });
    events.push(makeEvent("mine_deployed", now, `${player.name} deployed a mine`, { playerId: player.id, position: { ...player.position }, source: "mine" }));
  }

  consumeOneShotWeaponInput(room, player.id);
}

function updateProjectiles(room: RoomState, now: number, events: GameEvent[]): void {
  const dt = SIMULATION_STEP_MS / 1000;
  const survivors: ProjectileState[] = [];

  for (const projectile of room.projectiles) {
    if (now >= projectile.expiresAt) continue;
    const start = { ...projectile.position };

    if (projectile.kind === "homing" && projectile.targetId) {
      const target = room.players.find((player) => player.id === projectile.targetId && player.alive && player.connected);
      if (target) steerHomingProjectile(projectile, target, dt);
    }

    projectile.position.x += projectile.velocity.x * dt;
    projectile.position.y += projectile.velocity.y * dt;
    projectile.position.z += projectile.velocity.z * dt;

    const hit = room.players.find(
      (player) =>
        isDamageableTarget(room, projectile.ownerId, player) &&
        player.alive &&
        now >= player.invulnerableUntil &&
        segmentDistanceSq(start, projectile.position, player.position) <= (PLAYER_RADIUS + projectile.radius) ** 2
    );

    if (hit) {
      damagePlayer(room, hit, projectile.damage, projectile.ownerId, now, projectile.kind, events);
      continue;
    }

    survivors.push(projectile);
  }

  room.projectiles = survivors;
}

function updateMines(room: RoomState, now: number, events: GameEvent[]): void {
  const survivors: MineState[] = [];

  for (const mine of room.mines) {
    if (now >= mine.expiresAt) continue;
    const hit = room.players.find(
      (player) =>
        isDamageableTarget(room, mine.ownerId, player) &&
        player.alive &&
        now >= player.invulnerableUntil &&
        distanceSq(player.position, mine.position) <= (PLAYER_RADIUS + mine.radius) ** 2
    );

    if (hit) {
      damagePlayer(room, hit, mine.damage, mine.ownerId, now, "mine", events);
      continue;
    }

    survivors.push(mine);
  }

  room.mines = survivors;
}

function updatePickups(room: RoomState, now: number, events: GameEvent[]): void {
  const survivors: PickupState[] = [];

  for (const pickup of room.pickups) {
    if (now >= pickup.expiresAt) continue;
    const player = room.players.find(
      (candidate) =>
        candidate.alive &&
        candidate.connected &&
        distanceSq(candidate.position, pickup.position) <= (PLAYER_RADIUS + pickup.radius) ** 2
    );

    if (!player) {
      survivors.push(pickup);
      continue;
    }

    let value = 0;
    if (pickup.kind === "health") {
      player.health = Math.min(PLAYER_MAX_HEALTH, player.health + PLAYER_MAX_HEALTH * HEALTH_PICKUP_RESTORE_RATIO);
      value = Math.round(player.health);
    } else if (pickup.kind === "homing") {
      player.ammo.homing += HOMING_PICKUP_AMOUNT;
      value = player.ammo.homing;
    } else {
      player.ammo.mines += MINE_PICKUP_AMOUNT;
      value = player.ammo.mines;
    }

    events.push(
      makeEvent("pickup_collected", now, `${player.name} recovered ${pickup.kind === "mine" ? "mines" : pickup.kind}`, {
        playerId: player.id,
        value,
        position: { ...pickup.position },
        source: "pickup"
      })
    );
  }

  room.pickups = survivors;
}

function damagePlayer(
  room: RoomState,
  target: ServerPlayerState,
  damage: number,
  attackerId: string,
  now: number,
  source: NonNullable<GameEvent["source"]>,
  events: GameEvent[]
): void {
  target.health = Math.max(0, target.health - damage);
  events.push(
    makeEvent("player_damaged", now, `${target.name} took ${Math.round(damage)} damage`, {
      playerId: attackerId,
      targetId: target.id,
      value: Math.round(target.health),
      position: { ...target.position },
      source
    })
  );

  if (target.health > 0) return;

  const attacker = room.players.find((player) => player.id === attackerId);
  if (attacker && attacker.id !== target.id) {
    attacker.score += 1;
  }

  target.alive = false;
  target.respawnAt = now + RESPAWN_MS;
  target.velocity = { x: 0, y: 0, z: 0 };
  spawnPickup(room, target.position, now);
  events.push(
    makeEvent("player_sunk", now, `${target.name} was sunk${attacker ? ` by ${attacker.name}` : ""}`, {
      playerId: attackerId,
      targetId: target.id,
      value: attacker?.score ?? 0,
      position: { ...target.position },
      source
    })
  );

  if (attacker && scoreForWinCheck(room, attacker) >= room.config.scoreLimit) {
    finishMatch(room, now, events);
  }
}

function finishMatch(room: RoomState, now: number, events: GameEvent[]): void {
  if (room.phase === "finished") return;
  room.phase = "finished";
  room.finishedAt = now;
  room.projectiles = [];
  room.mines = [];
  room.pickups = [];

  const winner = chooseWinner(room);
  room.winnerId = winner?.playerId ?? null;
  room.winningTeamId = winner?.teamId ?? null;
  events.push(
    makeEvent("match_finished", now, winner ? `${winner.name} wins the round` : "Match finished", {
      playerId: winner?.playerId,
      value: winner?.score ?? 0
    })
  );
}

function createProjectile(room: RoomState, player: ServerPlayerState, kind: "regular" | "homing", now: number): ProjectileState {
  const forward = forwardFromRotation(player.rotation);
  const speed = kind === "regular" ? REGULAR_TORPEDO_SPEED : HOMING_TORPEDO_SPEED;
  const nose = {
    x: player.position.x + forward.x * (PLAYER_RADIUS + 1.2),
    y: player.position.y,
    z: player.position.z + forward.z * (PLAYER_RADIUS + 1.2)
  };

  const targetId = kind === "homing" ? chooseHomingTarget(room, { ownerId: player.id, position: player.position })?.id : undefined;

  return {
    id: `${room.tick}-${kind}-${player.id}-${now}`,
    ownerId: player.id,
    kind,
    position: nose,
    velocity: { x: forward.x * speed, y: 0, z: forward.z * speed },
    radius: PROJECTILE_RADIUS,
    damage: kind === "regular" ? REGULAR_TORPEDO_DAMAGE : HOMING_TORPEDO_DAMAGE,
    expiresAt: now + (kind === "regular" ? REGULAR_TORPEDO_LIFESPAN_MS : HOMING_TORPEDO_LIFESPAN_MS),
    targetId
  };
}

function chooseHomingTarget(room: RoomState, projectile: Pick<ProjectileState, "ownerId" | "position">): ServerPlayerState | undefined {
  let best: ServerPlayerState | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const player of room.players) {
    if (!isDamageableTarget(room, projectile.ownerId, player) || !player.alive || !player.connected) continue;
    const distance = distanceSq(player.position, projectile.position);
    if (distance < bestDistance) {
      best = player;
      bestDistance = distance;
    }
  }
  return best;
}

function spawnPickup(room: RoomState, position: Vector3State, now: number): void {
  const kind = choosePickupKind();
  if (!kind) return;
  room.pickups.push({
    id: `${room.tick}-pickup-${kind}-${now}-${Math.random().toString(36).slice(2, 7)}`,
    kind,
    position: { ...position },
    radius: PICKUP_RADIUS,
    expiresAt: now + PICKUP_LIFESPAN_MS
  });
}

function choosePickupKind(): PickupKind | null {
  const roll = Math.random();
  if (roll < 0.2) return "health";
  if (roll < 0.6) return "homing";
  if (roll < 0.8) return "mine";
  return null;
}

function consumeOneShotWeaponInput(room: RoomState, playerId: string): void {
  const input = room.inputs[playerId];
  if (!input || (!input.homing && !input.mine)) return;
  room.inputs[playerId] = { ...input, homing: false, mine: false };
}

function steerHomingProjectile(projectile: ProjectileState, target: ServerPlayerState, dt: number): void {
  const currentDirection = normalizeVector(projectile.velocity);
  const desiredDirection = normalizeVector(subtract(target.position, projectile.position));
  const angle = Math.acos(clamp(dot(currentDirection, desiredDirection), -1, 1));
  const turn = Math.min(1, (HOMING_TORPEDO_TURN_SPEED * dt) / Math.max(angle, 0.0001));
  const nextDirection = angle < 0.0001 ? desiredDirection : slerpUnitVector(currentDirection, desiredDirection, turn);
  projectile.velocity.x = nextDirection.x * HOMING_TORPEDO_SPEED;
  projectile.velocity.y = nextDirection.y * HOMING_TORPEDO_SPEED;
  projectile.velocity.z = nextDirection.z * HOMING_TORPEDO_SPEED;
}

function slerpUnitVector(a: Vector3State, b: Vector3State, t: number): Vector3State {
  const angle = Math.acos(clamp(dot(a, b), -1, 1));
  const sinAngle = Math.sin(angle);
  if (sinAngle < 0.0001) {
    return normalizeVector({
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      z: a.z + (b.z - a.z) * t
    });
  }
  const aScale = Math.sin((1 - t) * angle) / sinAngle;
  const bScale = Math.sin(t * angle) / sinAngle;
  return normalizeVector({
    x: a.x * aScale + b.x * bScale,
    y: a.y * aScale + b.y * bScale,
    z: a.z * aScale + b.z * bScale
  });
}

function resetPlayerForSpawn(player: ServerPlayerState, now: number, protect: boolean): void {
  const spawn = spawnForSlot(player.slot);
  player.position = positionFromSpawn(spawn);
  player.rotation = spawn.rotation;
  player.velocity = { x: 0, y: 0, z: 0 };
  player.health = PLAYER_MAX_HEALTH;
  player.ammo = { homing: PLAYER_INITIAL_HOMING, mines: PLAYER_INITIAL_MINES };
  player.alive = true;
  player.respawnAt = null;
  player.invulnerableUntil = protect ? now + SPAWN_PROTECTION_MS : now;
}

function nextOpenSlot(room: RoomState): number {
  const used = new Set(room.players.map((player) => player.slot));
  for (let slot = 0; slot < roomMaxPlayers(room); slot += 1) {
    if (!used.has(slot)) return slot;
  }
  return room.players.length % roomMaxPlayers(room);
}

function spawnForSlot(slot: number): SpawnPoint {
  return SPAWNS[slot % SPAWNS.length];
}

function positionFromSpawn(spawn: SpawnPoint): Vector3State {
  return { x: spawn.x, y: spawn.y, z: spawn.z };
}

function forwardFromRotation(rotation: number): Vector3State {
  return { x: -Math.cos(rotation), y: 0, z: Math.sin(rotation) };
}

function subtract(a: Vector3State, b: Vector3State): Vector3State {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function normalizeVector(vector: Vector3State): Vector3State {
  const length = Math.hypot(vector.x, vector.y, vector.z);
  if (length < 0.0001) return { x: -1, y: 0, z: 0 };
  return { x: vector.x / length, y: vector.y / length, z: vector.z / length };
}

function distanceSq(a: Vector3State, b: Vector3State): number {
  return (a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2;
}

function segmentDistanceSq(a: Vector3State, b: Vector3State, point: Vector3State): number {
  const ab = subtract(b, a);
  const lengthSq = dot(ab, ab);
  if (lengthSq < 0.0001) return distanceSq(a, point);
  const t = clamp(dot(subtract(point, a), ab) / lengthSq, 0, 1);
  const closest = {
    x: a.x + ab.x * t,
    y: a.y + ab.y * t,
    z: a.z + ab.z * t
  };
  return distanceSq(closest, point);
}

function dot(a: Vector3State, b: Vector3State): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

function ensureRoomCollections(room: RoomState): void {
  room.roomKind ??= "private";
  room.config = defaultRoomConfig({ ...room.config, maxPlayers: room.maxPlayers ?? room.config?.maxPlayers });
  room.maxPlayers = normalizeMaxPlayers(room.config.maxPlayers ?? room.maxPlayers ?? MAX_PLAYERS);
  if (room.roomKind === "auto") room.maxPlayers = AUTO_MATCH_PLAYERS;
  room.autoStartAt ??= null;
  room.winningTeamId ??= null;
  room.rematchRequestedBy ??= [];
  room.chat ??= [];
  room.projectiles ??= [];
  room.mines ??= [];
  room.pickups ??= [];
  room.inputs ??= {};
  room.disconnectGraceUntilByPlayer ??= {};
  for (const player of room.players ?? []) {
    player.profileId ??= null;
    player.teamId ??= teamForSlot(player.slot, room.config.mode);
    player.loadout ??= DEFAULT_LOADOUT;
  }
  assignTeams(room);
}

function roomKind(room: RoomState): RoomKind {
  return room.roomKind ?? "private";
}

function roomMaxPlayers(room: RoomState): number {
  if (roomKind(room) === "auto") return AUTO_MATCH_PLAYERS;
  return normalizeMaxPlayers(room.maxPlayers ?? MAX_PLAYERS);
}

function normalizeMaxPlayers(value: number): number {
  if (!Number.isFinite(value)) return MAX_PLAYERS;
  return Math.max(MIN_PLAYERS_TO_START, Math.min(MAX_PLAYERS, Math.floor(value)));
}

function normalizePositiveInteger(value: number | undefined, fallback: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(min, Math.min(max, Math.floor(value!)));
}

function normalizeGameMode(value: unknown): GameMode {
  return value === "team_deathmatch" ? "team_deathmatch" : "deathmatch";
}

function sanitizeMapSeed(value: string): string {
  return value.trim().replace(/[^A-Za-z0-9-]/g, "").slice(0, 32) || "classic-trench";
}

function sanitizeLoadout(loadout: PlayerLoadout): PlayerLoadout {
  const hull = loadout.hull === "scout" || loadout.hull === "support" ? loadout.hull : "assault";
  const skin = loadout.skin.trim().replace(/[^A-Za-z0-9-]/g, "").slice(0, 24) || "classic";
  return { hull, skin };
}

function sanitizeChatMessage(value: string): string {
  return value.trim().replace(/\s+/g, " ").slice(0, 120);
}

function assignTeams(room: RoomState): void {
  if (room.config.mode !== "team_deathmatch") {
    for (const player of room.players) player.teamId = null;
    return;
  }
  for (const player of room.players) {
    player.teamId = teamForSlot(player.slot, room.config.mode);
  }
}

function teamForSlot(slot: number, mode: GameMode): TeamId {
  if (mode !== "team_deathmatch") return null;
  return slot % 2 === 0 ? "blue" : "gold";
}

function isDamageableTarget(room: RoomState, attackerId: string, target: ServerPlayerState): boolean {
  if (target.id === attackerId) return false;
  if (room.config.mode !== "team_deathmatch" || room.config.friendlyFire) return true;
  const attacker = room.players.find((player) => player.id === attackerId);
  return !attacker || attacker.teamId === null || target.teamId === null || attacker.teamId !== target.teamId;
}

function chooseWinner(room: RoomState): { playerId: string; name: string; teamId: TeamId; score: number } | null {
  if (room.config.mode === "team_deathmatch") {
    const teamScores = new Map<NonNullable<TeamId>, number>();
    for (const player of room.players) {
      if (!player.teamId) continue;
      teamScores.set(player.teamId, (teamScores.get(player.teamId) ?? 0) + player.score);
    }
    const winningTeam = [...teamScores.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
    const mvp = [...room.players].filter((player) => player.teamId === winningTeam).sort((a, b) => b.score - a.score)[0];
    return mvp ? { playerId: mvp.id, name: `${winningTeam === "blue" ? "Blue" : "Gold"} team`, teamId: winningTeam, score: teamScores.get(winningTeam!) ?? mvp.score } : null;
  }

  const winner = [...room.players].sort((a, b) => b.score - a.score)[0];
  return winner ? { playerId: winner.id, name: winner.name, teamId: null, score: winner.score } : null;
}

function scoreForWinCheck(room: RoomState, player: ServerPlayerState): number {
  if (room.config.mode !== "team_deathmatch" || !player.teamId) return player.score;
  return room.players.filter((candidate) => candidate.teamId === player.teamId).reduce((total, candidate) => total + candidate.score, 0);
}

function applyFriction(value: number, friction: number, dt: number): number {
  const amount = friction * dt;
  if (Math.abs(value) <= amount) return 0;
  return value - Math.sign(value) * amount;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(min, Math.min(max, value));
}

function normalizeAngle(angle: number): number {
  const full = Math.PI * 2;
  return ((angle % full) + full) % full;
}

function makeEvent(
  type: GameEvent["type"],
  at: number,
  message: string,
  extras: Partial<Omit<GameEvent, "id" | "type" | "at" | "message">> = {}
): GameEvent {
  return {
    id: `${at}-${type}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    at,
    message,
    ...extras
  };
}
